import type {
  DistributionFormat,
  ScheduleItemObjectType,
} from "../constants/fast";
import type {
  AdBreakFormat,
  MediaFormat,
  MediaFormatWithAdBreak,
} from "../constants/media";
import type { Cuepoints } from "../types";
import type { Indexes } from "../types/common";
import type { WeekDays } from "../utils/fast";

export type GetChannelParams = string | number;

export interface UpdateChannelParams {
  name?: string;
  channelId?: string;
  data?: {
    name: string;
    first_day_of_week?: string;
    daily_start_time?: string;
    medias_colors?: Record<string, string>;
    logo_width?: number;
    logo_height?: number;
    logo_position?: string;
    channel_delivery_name?: string;
    channel_description?: string;
    channel_short_description?: string;
    channel_main_rating?: string;
    channel_delivery_regions?: string[];
    channel_genres?: string[];
  };
}

export interface GetScheduleByIdParams {
  scheduleId: string;
  channelId: string;
}

export interface GetScheduleScheduleListParams {
  scheduleId: string;
  channelId: string;
  periodStart: string;
  periodEnd: string;
}

export interface GetScheduleAllScheduleListParams {
  channelId: string;
  periodStart: string;
  periodEnd: string;
}

export interface GetScheduleAllScheduleListResponse {
  data: ScheduleItemWithScheduleId[];
  type: "schedules_lists";
}

export interface GetScheduleKpisParams {
  scheduleId: string;
  channelId: string;
  day?: string;
  month?: string;
  week?: string;
}

export interface GetScheduleKpisResponse {
  data: {
    ads_completion: number;
    ads_duration: number;
    ads_interprogram_duration: number;
    ads_interprogram_number: number;
    ads_interstitial_duration: number;
    ads_interstitial_number: number;
    programs_completion: number;
    programs_duration: number;
  };
  type: "kpis";
}

export interface GetSchedulesParams {
  channelId: string;
}

export interface UpdateSchedulesParams {
  channelId: string;
  updatedScheduleList: UpdatedSchedule[];
}

export interface AddScheduleListMediaParams {
  channelId: string;
  scheduleId: string;
  media: {
    object_uuid: string;
    start_time: string;
  };
}

export interface DeleteScheduleListMediaParams {
  channelId: string;
  uuid: string;
  scheduleId: string;
}

export type DeleteScheduleListMediaResponse = string;

export interface UpdateScheduleListMediaParams {
  channelId: string;
  uuid: string;
  scheduleId: string;
  media: {
    start_time: string;
  };
}

export interface UpdateScheduleListMediaResponse {
  data: ScheduleItem;
  type: "scheduled_program";
}

export interface ScheduleItemWithScheduleId extends ScheduleItem {
  fast_channel_schedule_uuid: string;
  fast_channel_timeslot_uuid?: string;
  fast_channel_timeslot_name?: string;
  fast_channel_timeslot_color?: string;
}

export interface GetSchedulePreviewParams {
  channelId: string;
  scheduleId: string;
  uuid: string;
}

export interface GetSchedulePreviewResponse {
  url: string;
  cuepoints: Cuepoints;
}

export interface InterstitialAdbreakConfig {
  adbreaks_at: { end_time: string; start_time: string }[] | null;
  cuepoints: Cuepoints | null;
}

export interface ScheduleItem {
  uuid: string;
  object_label: string;
  start_time: string;
  end_time: string;
  object_type: ScheduleItemObjectType;
  object_uuid: string;
  object_duration: number;
  object_total_duration: number;
  object_format: MediaFormatWithAdBreak | null;
  object_picture: string | null;
  interstitial_adbreak_config: InterstitialAdbreakConfig;
  interstitial_adbreak_uuid: string | null;
  updated_at: string;
  user_uuid: string;
}

export interface ScheduleList {
  type: "schedule_list";
  data: ScheduleItem[];
}

export interface GetChannelResponse {
  uuid: string;
  channel_delivery_name: string;
  channel_description: string | null;
  channel_short_description: string | null;
  channel_main_rating: string | null;
  channel_delivery_regions: string[] | null;
  channel_genres: string[] | null;
  name: string;
  version: string;
  timezone?: string;
  language_uuid?: string;
  first_day_of_week?: WeekDays.MONDAY | WeekDays.SUNDAY | WeekDays.SATURDAY;
  daily_start_time?: string;
  medias_colors?: Record<MediaFormatWithAdBreak, string>;
  logo_url?: string;
  logo_width?: number;
  logo_height?: number;
  logo_position?: "top_left" | "top_right" | "bottom_left" | "bottom_right";
  scte_id?: string;
}

export type GetScheduleByIdResponse = ScheduleList;

export type GetScheduleScheduleListResponse = ScheduleList;

export interface GetChannelsListResponse {
  data: FastChannelListItem[];
  indexes: Indexes;
  page: Page;
  type: "fast_channels";
}

export interface FastChannelListItem {
  broadcasts_count: number;
  logo_url: string;
  name: string;
  schedules_count: number;
  uuid: string;
  version: string;
  version_label: string;
}

export interface Page {
  number: number;
  total: number;
  total_items: number;
}

export interface AddScheduleListMediaResponse {
  data: ScheduleItem;
}

export interface GetSchedulesResponse {
  data: Schedule[];
  type: "schedules";
}

export interface GetCalendarFillingParams {
  channelId: string;
  scheduleId: string;
  periodStart: string;
  periodEnd: string;
}

export interface DayCalendarFilling {
  scheduled_time: number;
  template_used: boolean;
}

export interface GetCalendarFillingResponse {
  data: Record<string, DayCalendarFilling>;
  type: "calendar_filling";
}

export interface Schedule {
  color: string;
  daily_ads_objective: number;
  daily_programs_objective: number;
  default: boolean;
  framerate: number;
  hls_url: string | null;
  inserted_at: Date;
  name: string;
  uuid: string;
  filler_url: string;
  baseline_filler: boolean;
  broadcasts: (ScheduleBroadcast | OVPBroadcast)[];
}

export interface ScheduleBroadcast {
  type: "okast";
  ad_service: string;
  broadcast_url: string | null;
  broadcaster: null;
  distribution_format: DistributionFormat;
  name: string;
  uuid: string;
  broadcaster_name: string;
  broadcaster_logo_url: string;
  output_network: string;
  epg_url: string;
  channel_list_url: string;
}

export interface OVPBroadcast extends Omit<ScheduleBroadcast, "type"> {
  type: "ovp";
  embed_url: string;
}

export type UpdatedSchedule = Pick<
  Schedule,
  "uuid" | "name" | "color" | "daily_ads_objective" | "daily_programs_objective"
>;

export interface GetScheduleEPGParams {
  channelId: string;
  scheduleId: string;
  periodStart?: string;
  periodEnd?: string;
}

interface EPGItem {
  end_time: string;
  fast_channel_schedule_uuid: string;
  object_duration: number;
  object_label: string;
  object_type: ScheduleItemObjectType;
  object_uuid: string;
  start_time: string;
  uuid: string;
  object_picture: string | null;
}
export type GetScheduleEPGResponse = EPGItem[];
export interface DeleteScheduleParams {
  channelId: string;
  scheduleId: string;
  date: string;
}

export type DeleteScheduleResponse = string;

export interface GetAdBreaksListResponse {
  data: AdBreakItem[];
  indexes: Indexes;
  page: Page;
  type: "adbreaks";
}
export interface GetAdBreakResponse {
  data: AdBreakItemSingle;
  type: "ad_break";
}

export interface AdBreakItemSingle {
  ad_replacement_duration: number;
  adbreak_duration: number;
  name: string;
  uuid: string;
  medias: AdBreakMedia[];
  is_deletable: boolean;
  is_editable: boolean;
  is_archivable: boolean;
}
export interface AdBreakItem {
  duration: number;
  name: string;
  uuid: string;
  inserted_at: string;
  updated_at: string;
}

export interface DeleteAdBreakResponse {
  message: string;
}
export interface ArchiveAdBreakResponse {
  message: "adbreak_archived";
}

export interface UpdatedAdBreak {
  name: string;
  medias: AdBreakMedia[];
}

export interface Broadcast {
  broadcast_url: string;
  fast_channel_name: string;
  name: string;
  uuid: string;
}

export interface GetBroadcastsResponse {
  data: Broadcast[];
  indexes: Indexes;
  page: Page;
  type: "broadcasts";
}

export type BroadcastAdService = "okast";

export interface UpdateBroadcastResponse {
  data: OVPBroadcast;
  type: "broadcasts";
}

export interface GetScheduleDetailsParams {
  channelId: string;
  day: string;
}
export interface GetScheduleDetailsResponse {
  data: {
    variants: ScheduleVariantDetails[];
    timeslots: ScheduleTimeslotDetails[];
  };
  type: "schedule_variant";
}

export interface ScheduleTimeslotDetails {
  end_time: string;
  name: string;
  uuid: string;
  items: number;
  start_time: string;
  color: string;
}

export interface ScheduleVariantDetails {
  duration: number;
  items: number;
  name: string;
  uuid: string;
}

export interface Playlist {
  updated_at: string;
  total_items: number;
  name: string;
  uuid: string;
}

export interface GetPlaylistListResponse {
  data: Playlist[];
  indexes: Indexes;
  page: Page;
  type: "fast_playlists";
}

interface ListMedia<T> {
  duration: number;
  format?: T;
  label: string;
  media_uuid: string;
  rank: number;
}

export type AdBreakMedia = ListMedia<AdBreakFormat>;

export type PlaylistDropzoneMedia = ListMedia<MediaFormat>;

export interface GetPlaylistResponse {
  name: string;
  uuid: string;
  items: PlaylistDropzoneMedia[];
  total_items: number;
  inserted_at: string;
  updated_at: string;
}

export interface UpdatedPlaylist {
  name: string;
  items: {
    media_uuid: string;
    rank: number;
  }[];
}

export interface CreatePlaylistParams {
  channelId: string;
  updatedPlaylist: UpdatedPlaylist;
}
export interface UpdatePlaylistParams extends CreatePlaylistParams {
  playlistId: string;
}

export enum FillingType {
  Loop = "loop",
  SingleRun = "single_run",
}

export interface TimeslotItem {
  color: string;
  duration_ms: number;
  inserted_at: string;
  name: string;
  total_rules: number;
  type: FillingType;
  updated_at: string;
  uuid: string;
  filling_playlist_uuid: string | null;
  interstitial_adbreak_uuid: string | null;
  is_editable: boolean;
  is_deletable: boolean;
  is_archivable: boolean;
  rules: TimeslotRule[];
}

export type TimeslotListItem = Pick<
  TimeslotItem,
  | "color"
  | "duration_ms"
  | "inserted_at"
  | "name"
  | "total_rules"
  | "type"
  | "updated_at"
  | "uuid"
>;
export interface GetTimeslotList {
  data: TimeslotListItem[];
  indexes: Indexes;
  page: Page;
  type: "fast_channels_timeslots";
}

export enum TimeslotRuleMode {
  Specific = "specific",
  Random = "random",
  Adbreak = "adbreak",
  Sequential = "sequential",
}

export enum TemplateSlotObject {
  AdBreak = "adbreak",
  FastChannelTimeslot = "fast_channel_timeslot",
}

interface TimeslotBaseRule {
  uuid: string;
  rank: number;
}

export interface TimeslotSpecificRule extends TimeslotBaseRule {
  mode: TimeslotRuleMode.Specific;
  media_uuid: string;
  elements?: 1;
}

export interface TimeslotAdbreakRule extends TimeslotBaseRule {
  mode: TimeslotRuleMode.Adbreak;
  adbreak_uuid: string;
  elements?: 1;
}

export interface TimeslotRandomRule extends TimeslotBaseRule {
  mode: TimeslotRuleMode.Random;
  fast_channel_playlist_uuid: string;
  adbreak_uuid: string | null;
  elements: number;
}

export interface TimeslotSequentialRule extends TimeslotBaseRule {
  mode: TimeslotRuleMode.Sequential;
  adbreak_uuid: string | null;
  elements: number;
  sequential_delta: number; // Max is ${elements} - 1
  sequential_start_index: number;
  fast_channel_playlist_uuid: string;
}

export type TimeslotRule =
  | TimeslotSpecificRule
  | TimeslotRandomRule
  | TimeslotAdbreakRule
  | TimeslotSequentialRule;

export type GetTimeslotResponse = TimeslotItem;

export type UpdatedTimeslot = Pick<
  GetTimeslotResponse,
  | "name"
  | "rules"
  | "color"
  | "duration_ms"
  | "type"
  | "filling_playlist_uuid"
  | "interstitial_adbreak_uuid"
>;
export interface GetTemplatesListResponse {
  data: Template[];
  indexes: Indexes;
  page: Page;
  type: "fast_channels_templates";
}

export interface Template {
  uuid: string;
  name: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
  updated_at: string;
  inserted_at: string;
  slots_errors_count: number;
  slots_count: number;
}

export interface SchedulingParameters {
  monday: string | null;
  tuesday: string | null;
  wednesday: string | null;
  thursday: string | null;
  friday: string | null;
  saturday: string | null;
  sunday: string | null;
  period_start_date: string;
  period_end_date: string;
  period_start_time?: string;
}

export interface GetTemplateResponse {
  friday: boolean;
  inserted_at: Date;
  monday: boolean;
  name: string;
  saturday: boolean;
  slots: (TimeslotSlot | AdBreakSlot)[];
  slots_errors: SlotError[];
  sunday: boolean;
  thursday: boolean;
  tuesday: boolean;
  updated_at: Date;
  uuid: string;
  wednesday: boolean;
  is_editable: boolean;
  is_deletable: boolean;
  is_archivable: boolean;
}

export interface AdBreakSlot {
  adbreak: Pick<AdBreakItem, "duration" | "name" | "uuid">;
  adbreak_uuid: string;
  object: TemplateSlotObject.AdBreak;
  start_time: string;
}

export interface UpdatedAdBreakSlot extends Pick<
  AdBreakSlot,
  "start_time" | "adbreak_uuid" | "object"
> {
  adbreak: {
    name: string;
  };
}

export interface TimeslotSlot {
  fast_channel_timeslot: {
    duration: number;
    name: string;
    uuid: string;
    color: string;
    type: FillingType;
  };
  fast_channel_timeslot_uuid: string;
  object: TemplateSlotObject.FastChannelTimeslot;
  start_time: string;
}

export interface UpdatedTimeslotSlot extends Omit<
  TimeslotSlot,
  "fast_channel_timeslot"
> {
  fast_channel_timeslot: Omit<TimeslotSlot["fast_channel_timeslot"], "type">;
}

export interface SlotError {
  adbreak_uuid: string | null;
  fast_channel_timeslot_uuid: string | null;
  start_time: string;
}

export interface GetTemplatesAssetListResponse {
  data: (TemplatesAssetsAdBreakItem | TemplatesAssetsTimeSlotItem)[];
  indexes: Indexes;
  page: Page;
  type: "fast_channels_templates_assets";
}

export interface TemplatesAssetsTimeSlotItem {
  color: string;
  duration_ms: number;
  name: string;
  object: "timeslot";
  uuid: string;
  type: FillingType;
  updated_at: string;
}

export interface TemplatesAssetsAdBreakItem extends AdBreakItem {
  object: "adbreak";
  updated_at: string;
}

export interface UpdatedTemplate extends Pick<
  GetTemplateResponse,
  | "name"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday"
> {
  slots: (UpdatedTimeslotSlot | UpdatedAdBreakSlot)[];
}

export interface DeleteScheduleListTimeslotParams {
  channelId: string;
  scheduleId: string;
  periodStart: string;
  periodEnd: string;
}

export type DeleteScheduleListTimeslotResponse = string;
export interface GetCalendarFillingWeekParams {
  channelId: string;
  week: string;
  variants?: boolean;
}

export interface DayWeekCalendarFillingPicture {
  cover_picture_16_6: string;
  default: string;
}

export interface CalendarEventFilling {
  end_time: string;
  fast_channel_schedule_uuid: string;
  object_duration: number;
  object_format: MediaFormat | null;
  object_label: string;
  object_picture: string | null;
  object_uuid: string;
  start_time: string;
  uuid: string;
  interstitial_adbreak_config: InterstitialAdbreakConfig | null;
  interstitial_adbreak_uuid: string | null;
}

export interface GetCalendarFillingWeekResponse {
  data: Record<string, CalendarEventFilling[]>;
  type: "calendar_week_filling";
}

export interface RatingOption {
  label: string;
  value: string;
}

export type GetRatingListResponse = RatingOption[];
